import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import StatCard from "../dashboard/StatCard";
import DashboardSection from "../dashboard/DashboardSection";
import EmailInboundService from "../../services/EmailInboundService";
import MessageTemplateService from "../../services/MessageTemplateService";
import { errorMessage } from "../../shared/utils/validators";
import { inferTemplateVariableSlots } from "../../utils/templateVariableSamples";
import MailboxDialog from "./MailboxDialog";
import FetchPastDialog from "./FetchPastDialog";
import RuleDialog from "./RuleDialog";
import {
  BACKFILL_RANGE_OPTIONS,
  EMPTY_RULE,
  PERIOD_OPTIONS,
  defaultEnquiryVariablePaths,
  emptyTemplateVariables,
  formatWhen,
  mailboxStatusChip,
  statusColor,
  templateDisplayName,
} from "./helpers";

const RULES_PER_PAGE = 10;

export default function InboundEmailDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("today");
  const [summary, setSummary] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [filterConfigId, setFilterConfigId] = useState("");
  const [tab, setTab] = useState(0);

  const [rules, setRules] = useState([]);
  const [rulePage, setRulePage] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [ruleForm, setRuleForm] = useState(EMPTY_RULE);
  const [savingRule, setSavingRule] = useState(false);

  const [events, setEvents] = useState([]);
  const [eventPage, setEventPage] = useState(0);
  const [eventTotal, setEventTotal] = useState(0);
  const [eventLimit] = useState(25);

  const [pollRuns, setPollRuns] = useState([]);
  const [pollPage, setPollPage] = useState(0);
  const [pollTotal, setPollTotal] = useState(0);
  const [pollLimit] = useState(25);

  const [mailboxDialogOpen, setMailboxDialogOpen] = useState(false);
  const [editingMailbox, setEditingMailbox] = useState(null);
  const [savingMailbox, setSavingMailbox] = useState(false);
  const [busyMailboxId, setBusyMailboxId] = useState(null);
  const [fetchPastMailbox, setFetchPastMailbox] = useState(null);
  const [fetchPastBusy, setFetchPastBusy] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnack = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const loadSummary = useCallback(async (nextPeriod = period) => {
    const data = await EmailInboundService.getSummary(nextPeriod);
    setSummary(data || null);
    if (Array.isArray(data?.mailboxes)) setMailboxes(data.mailboxes);
  }, [period]);

  const loadEvents = useCallback(async (page = 0, configId = filterConfigId) => {
    const result = await EmailInboundService.getEvents({
      page: page + 1,
      limit: eventLimit,
      configId: configId || undefined,
    });
    setEvents(Array.isArray(result?.items) ? result.items : []);
    setEventTotal(Number(result?.total) || 0);
    setEventPage(page);
  }, [eventLimit, filterConfigId]);

  const loadPollRuns = useCallback(async (page = 0, configId = filterConfigId) => {
    const result = await EmailInboundService.getPollRuns({
      page: page + 1,
      limit: pollLimit,
      configId: configId || undefined,
    });
    setPollRuns(Array.isArray(result?.items) ? result.items : []);
    setPollTotal(Number(result?.total) || 0);
    setPollPage(page);
  }, [pollLimit, filterConfigId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, ruleList, eventPageData, pollPageData, templateList] = await Promise.all([
        EmailInboundService.getSummary(period),
        EmailInboundService.getRules(),
        EmailInboundService.getEvents({ page: 1, limit: eventLimit, configId: filterConfigId || undefined }),
        EmailInboundService.getPollRuns({ page: 1, limit: pollLimit, configId: filterConfigId || undefined }),
        MessageTemplateService.getAllTemplates().catch(() => []),
      ]);
      setSummary(sum || null);
      setMailboxes(Array.isArray(sum?.mailboxes) ? sum.mailboxes : []);
      setRules(Array.isArray(ruleList) ? ruleList : []);
      setEvents(Array.isArray(eventPageData?.items) ? eventPageData.items : []);
      setEventTotal(Number(eventPageData?.total) || 0);
      setEventPage(0);
      setPollRuns(Array.isArray(pollPageData?.items) ? pollPageData.items : []);
      setPollTotal(Number(pollPageData?.total) || 0);
      setPollPage(0);
      setTemplates(Array.isArray(templateList) ? templateList : []);
    } catch (err) {
      showSnack(errorMessage(err) || "Failed to load inbound email", "error");
    } finally {
      setLoading(false);
    }
  }, [period, eventLimit, pollLimit, filterConfigId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const selectMailbox = (id) => {
    setFilterConfigId((prev) => (prev === id ? "" : id));
  };

  const saveMailbox = async (form) => {
    if (!String(form.imap_username || "").trim()) {
      showSnack("Username is required", "error");
      return;
    }
    setSavingMailbox(true);
    try {
      const payload = {
        name: form.name,
        enabled: form.enabled,
        imap_host: form.imap_host,
        imap_port: form.imap_port,
        imap_username: form.imap_username,
        imap_mailbox: form.imap_mailbox,
        imap_secure: form.imap_secure,
        default_country_code: form.default_country_code,
        poll_interval_sec: form.poll_interval_sec,
      };
      if (form.imap_password) payload.imap_password = form.imap_password;
      if (editingMailbox?.id) {
        await EmailInboundService.updateMailbox(editingMailbox.id, payload);
        showSnack("Mailbox saved");
      } else {
        await EmailInboundService.createMailbox(payload);
        showSnack("Mailbox added");
      }
      setMailboxDialogOpen(false);
      setEditingMailbox(null);
      await loadSummary();
    } catch (err) {
      showSnack(errorMessage(err) || "Failed to save mailbox", "error");
    } finally {
      setSavingMailbox(false);
    }
  };

  const toggleMailbox = async (mailbox, enabled) => {
    try {
      await EmailInboundService.updateMailbox(mailbox.id, { ...mailbox, enabled });
      await loadSummary();
    } catch (err) {
      showSnack(errorMessage(err) || "Failed to update mailbox", "error");
    }
  };

  const processMailbox = async (mailbox) => {
    setBusyMailboxId(mailbox.id);
    try {
      const result = await EmailInboundService.pollNewEmails(mailbox.id);
      const n = Number(result?.processed || 0);
      showSnack(n > 0 ? `Processed ${n} new email${n === 1 ? "" : "s"}` : "No new unread emails");
      await Promise.all([loadSummary(), loadEvents(eventPage), loadPollRuns(0)]);
    } catch (err) {
      showSnack(errorMessage(err) || "Failed to process emails", "error");
    } finally {
      setBusyMailboxId(null);
    }
  };

  const fetchPast = async ({ range, unread, read }) => {
    const mailbox = fetchPastMailbox;
    if (!mailbox) return;
    setFetchPastBusy(true);
    setBusyMailboxId(mailbox.id);
    try {
      let afterUid = 0;
      let totalProcessed = 0;
      let loops = 0;
      let hasMore = true;
      while (hasMore && loops < 15) {
        const result = await EmailInboundService.backfill({
          range,
          unread,
          read,
          afterUid,
          mailboxId: mailbox.id,
        });
        totalProcessed += Number(result?.processed || 0);
        hasMore = !!result?.hasMore;
        afterUid = Number(result?.nextUid || afterUid);
        loops += 1;
      }
      const label = BACKFILL_RANGE_OPTIONS.find((opt) => opt.value === range)?.label || range;
      const base = totalProcessed > 0
        ? `Fetched ${totalProcessed} email${totalProcessed === 1 ? "" : "s"} · ${label}`
        : `No matching emails · ${label}`;
      showSnack(hasMore ? `${base} · more remaining, run again` : base);
      setFetchPastMailbox(null);
      await Promise.all([loadSummary(), loadEvents(0), loadPollRuns(0)]);
    } catch (err) {
      showSnack(errorMessage(err) || "Failed to fetch past emails", "error");
    } finally {
      setFetchPastBusy(false);
      setBusyMailboxId(null);
    }
  };

  const removeMailbox = async (mailbox) => {
    if (!window.confirm(`Delete mailbox "${mailbox.name || mailbox.imap_username}"? Activity log is kept.`)) return;
    try {
      await EmailInboundService.deleteMailbox(mailbox.id);
      if (filterConfigId === mailbox.id) setFilterConfigId("");
      showSnack("Mailbox deleted");
      await loadAll();
    } catch (err) {
      showSnack(errorMessage(err) || "Failed to delete mailbox", "error");
    }
  };

  const openEditRule = (rule) => {
    const selected = templates.find((t) => (t.getId?.() || t.id) === rule.template_id);
    const storedVars = rule.template_variables && typeof rule.template_variables === "object"
      ? {
        header: rule.template_variables.header || {},
        body: rule.template_variables.body || {},
        footer: rule.template_variables.footer || {},
        buttons: rule.template_variables.buttons || {},
      }
      : emptyTemplateVariables();
    const hasStoredMaps = ["header", "body", "footer"].some(
      (section) => Object.keys(storedVars[section] || {}).length > 0,
    );
    setEditingRuleId(rule.id);
    setRuleForm({
      name: rule.name || "",
      subject_match_type: rule.subject_match_type || "contains",
      subject_pattern: rule.subject_pattern || "",
      action_type: rule.action_type || "template",
      template_id: rule.template_id || "",
      template_variables: hasStoredMaps || !selected
        ? storedVars
        : defaultEnquiryVariablePaths(inferTemplateVariableSlots(selected)),
      message_text: rule.message_text || "",
      parser_type: rule.parser_type || "phones_only",
      create_lead: !!rule.create_lead,
      send_whatsapp: rule.send_whatsapp !== false,
      send_email: !!rule.send_email,
      email_subject: rule.email_subject || "",
      email_body: rule.email_body || "",
      enabled: !!rule.enabled,
      priority: Number(rule.priority ?? 100),
    });
    setRuleDialogOpen(true);
  };

  const saveRule = async () => {
    if (!String(ruleForm.name || "").trim()) {
      showSnack("Rule name is required", "error");
      return;
    }
    if (!String(ruleForm.subject_pattern || "").trim()) {
      showSnack("Subject pattern is required", "error");
      return;
    }
    setSavingRule(true);
    try {
      const payload = {
        ...ruleForm,
        template_id: ruleForm.action_type === "template" ? ruleForm.template_id : null,
        priority: Number(ruleForm.priority) || 100,
      };
      if (editingRuleId) await EmailInboundService.updateRule(editingRuleId, payload);
      else await EmailInboundService.createRule(payload);
      setRuleDialogOpen(false);
      showSnack(editingRuleId ? "Rule updated" : "Rule created");
      const ruleList = await EmailInboundService.getRules();
      setRules(Array.isArray(ruleList) ? ruleList : []);
    } catch (err) {
      showSnack(errorMessage(err) || "Failed to save rule", "error");
    } finally {
      setSavingRule(false);
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await EmailInboundService.deleteRule(rule.id);
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
      showSnack("Rule deleted");
    } catch (err) {
      showSnack(errorMessage(err) || "Failed to delete rule", "error");
    }
  };

  const toggleRuleEnabled = async (rule) => {
    try {
      const updated = await EmailInboundService.updateRule(rule.id, { ...rule, enabled: !rule.enabled });
      setRules((prev) => prev.map((r) => (r.id === rule.id ? updated : r)));
    } catch (err) {
      showSnack(errorMessage(err) || "Failed to update rule", "error");
    }
  };

  const clearEvents = async () => {
    if (!window.confirm("Clear all activity log entries for this service?")) return;
    try {
      await EmailInboundService.clearEvents();
      setEvents([]);
      setEventTotal(0);
      showSnack("Activity log cleared");
      await loadSummary();
    } catch (err) {
      showSnack(errorMessage(err) || "Failed to clear log", "error");
    }
  };

  const periodLabel = PERIOD_OPTIONS.find((p) => p.value === period)?.label || "Today";
  const pagedRules = rules.slice(rulePage * RULES_PER_PAGE, rulePage * RULES_PER_PAGE + RULES_PER_PAGE);
  const anyError = mailboxes.some((m) => m.imap_last_error);

  if (loading && !summary) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="h5" fontWeight={700}>Inbound Email</Typography>
            <Chip
              size="small"
              label={anyError ? "Error" : mailboxes.some((m) => m.enabled) ? "Live" : "Paused"}
              color={anyError ? "error" : mailboxes.some((m) => m.enabled) ? "success" : "default"}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Mailboxes, shared rules, and processing activity
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Period</InputLabel>
            <Select
              label="Period"
              value={period}
              onChange={async (e) => {
                const next = e.target.value;
                setPeriod(next);
                try {
                  await loadSummary(next);
                } catch (err) {
                  showSnack(errorMessage(err) || "Failed to load summary", "error");
                }
              }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={loadAll} aria-label="Refresh"><RefreshIcon /></IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingMailbox(null);
              setMailboxDialogOpen(true);
            }}
          >
            Add mailbox
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard title="Mailboxes on" value={summary?.mailboxes_enabled ?? 0} subtitle={`${summary?.mailboxes_total ?? 0} total`} icon={EmailOutlinedIcon} accent="primary" />
        <StatCard title="Processed" value={summary?.processed ?? 0} subtitle={periodLabel} icon={CheckCircleOutlineIcon} accent="violet" />
        <StatCard title="Leads created" value={summary?.leads ?? 0} subtitle={periodLabel} icon={PersonAddAltIcon} accent="success" />
        <StatCard title="WhatsApp sent" value={summary?.sent ?? 0} subtitle={periodLabel} icon={SendIcon} accent="primary" />
        <StatCard title="Skipped" value={summary?.skipped ?? 0} subtitle={periodLabel} icon={WarningAmberIcon} accent="warning" />
        <StatCard title="Failed" value={summary?.failed ?? 0} subtitle={summary?.last_polled_at ? `Last poll ${formatWhen(summary.last_polled_at)}` : "No poll yet"} icon={ErrorOutlineIcon} accent="error" />
      </Box>

      <DashboardSection title="Mailboxes" description="Click a card to filter activity. Rules are shared across every inbox.">
        {mailboxes.length === 0 && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary" sx={{ mb: 1.5 }}>No mailboxes yet.</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingMailbox(null); setMailboxDialogOpen(true); }}>
              Add mailbox
            </Button>
          </Paper>
        )}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          {mailboxes.map((mailbox) => {
            const chip = mailboxStatusChip(mailbox);
            const selected = filterConfigId === mailbox.id;
            return (
              <Paper
                key={mailbox.id}
                variant="outlined"
                onClick={() => selectMailbox(mailbox.id)}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  borderColor: selected ? "primary.main" : "divider",
                  boxShadow: selected ? "0 0 0 1px #1976d2" : "none",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={600} noWrap>{mailbox.name || mailbox.imap_username || "Mailbox"}</Typography>
                      <Chip size="small" label={chip.label} color={chip.color} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {mailbox.imap_username || "No username"} · {mailbox.imap_mailbox || "INBOX"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last poll {formatWhen(mailbox.imap_last_polled_at)}
                    </Typography>
                    {mailbox.imap_last_error && (
                      <Alert severity="error" sx={{ mt: 1, py: 0 }}>{mailbox.imap_last_error}</Alert>
                    )}
                  </Box>
                  <Switch
                    checked={!!mailbox.enabled}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => toggleMailbox(mailbox, e.target.checked)}
                  />
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Button size="small" startIcon={<EditIcon />} onClick={(e) => { e.stopPropagation(); setEditingMailbox(mailbox); setMailboxDialogOpen(true); }}>Edit</Button>
                  <Button size="small" startIcon={<MarkEmailUnreadIcon />} disabled={busyMailboxId === mailbox.id} onClick={(e) => { e.stopPropagation(); processMailbox(mailbox); }}>
                    {busyMailboxId === mailbox.id ? "…" : "Process now"}
                  </Button>
                  <Button size="small" startIcon={<HistoryIcon />} disabled={busyMailboxId === mailbox.id} onClick={(e) => { e.stopPropagation(); setFetchPastMailbox(mailbox); }}>
                    Fetch past
                  </Button>
                  <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={(e) => { e.stopPropagation(); removeMailbox(mailbox); }}>Delete</Button>
                </Stack>
              </Paper>
            );
          })}
        </Box>
        {filterConfigId && (
          <Button size="small" sx={{ mt: 1 }} onClick={() => selectMailbox(filterConfigId)}>
            Clear mailbox filter
          </Button>
        )}
      </DashboardSection>

      <Paper variant="outlined" sx={{ mt: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
          <Tab label={`Activity (${eventTotal})`} />
          <Tab label={`Rules (${rules.length})`} />
          <Tab label={`Poll runs (${pollTotal})`} />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Stack direction="row" justifyContent="flex-end" sx={{ px: 2, pt: 1 }}>
              <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={eventTotal === 0} onClick={clearEvents}>
                Clear log
              </Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Mailbox</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">No inbound emails processed yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {events.map((event) => (
                  <TableRow
                    key={event.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/inbound-email/events/${event.id}`)}
                  >
                    <TableCell>{formatWhen(event.updated_at || event.created_at)}</TableCell>
                    <TableCell>{event.mailbox_name || "—"}</TableCell>
                    <TableCell>{event.from_address || "—"}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 360 }}>{event.subject || "—"}</Typography>
                      {event.parsed?.name && (
                        <Typography variant="caption" color="text.secondary">{event.parsed.name}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={event.status} color={statusColor(event.status)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={eventTotal}
              page={eventPage}
              onPageChange={(_, p) => loadEvents(p)}
              rowsPerPage={eventLimit}
              rowsPerPageOptions={[eventLimit]}
            />
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Stack direction="row" justifyContent="flex-end" sx={{ px: 2, pt: 1 }}>
              <Button size="small" startIcon={<AddIcon />} onClick={() => { setEditingRuleId(null); setRuleForm({ ...EMPTY_RULE }); setRuleDialogOpen(true); }}>
                Create rule
              </Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Match / Parser</TableCell>
                  <TableCell>Actions</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRules.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">No rules yet. Create one for IndiaMART enquiry → lead + WA.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {pagedRules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>{rule.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{rule.subject_match_type}: &quot;{rule.subject_pattern}&quot;</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {rule.parser_type === "indiamart_enquiry" ? "IndiaMART" : "Phones only"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {rule.create_lead && <Chip size="small" label="Lead" />}
                        {rule.send_whatsapp !== false && (
                          <Chip size="small" color="success" label={`WA: ${templateDisplayName(templates.find((t) => (t.getId?.() || t.id) === rule.template_id))}`} />
                        )}
                        {rule.send_email && <Chip size="small" label="Email" color="info" />}
                      </Stack>
                    </TableCell>
                    <TableCell>{rule.priority}</TableCell>
                    <TableCell>
                      <Switch size="small" checked={!!rule.enabled} onChange={() => toggleRuleEnabled(rule)} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<EditIcon />} onClick={() => openEditRule(rule)}>Edit</Button>
                      <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => deleteRule(rule)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={rules.length}
              page={rulePage}
              onPageChange={(_, p) => setRulePage(p)}
              rowsPerPage={RULES_PER_PAGE}
              rowsPerPageOptions={[RULES_PER_PAGE]}
            />
          </Box>
        )}

        {tab === 2 && (
          <Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Mailbox</TableCell>
                  <TableCell>Trigger</TableCell>
                  <TableCell>Fetched</TableCell>
                  <TableCell>Processed</TableCell>
                  <TableCell>Skipped</TableCell>
                  <TableCell>Failed</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pollRuns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">No poll runs yet. Process now or wait for auto-poll.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {pollRuns.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell>{formatWhen(run.started_at)}</TableCell>
                    <TableCell>{run.mailbox_name || "—"}</TableCell>
                    <TableCell><Chip size="small" label={run.trigger} /></TableCell>
                    <TableCell>{run.fetched}</TableCell>
                    <TableCell>{run.processed}</TableCell>
                    <TableCell>{Number(run.skipped_duplicate || 0) + Number(run.skipped_no_rule || 0)}</TableCell>
                    <TableCell>{run.failed}</TableCell>
                    <TableCell>
                      {run.error_message ? (
                        <Tooltip title={run.error_message}>
                          <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 220 }}>{run.error_message}</Typography>
                        </Tooltip>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={pollTotal}
              page={pollPage}
              onPageChange={(_, p) => loadPollRuns(p)}
              rowsPerPage={pollLimit}
              rowsPerPageOptions={[pollLimit]}
            />
          </Box>
        )}
      </Paper>

      <MailboxDialog
        open={mailboxDialogOpen}
        mailbox={editingMailbox}
        saving={savingMailbox}
        onClose={() => { setMailboxDialogOpen(false); setEditingMailbox(null); }}
        onSave={saveMailbox}
      />
      <FetchPastDialog
        open={!!fetchPastMailbox}
        mailbox={fetchPastMailbox}
        busy={fetchPastBusy}
        onClose={() => setFetchPastMailbox(null)}
        onSubmit={fetchPast}
      />
      <RuleDialog
        open={ruleDialogOpen}
        editing={!!editingRuleId}
        form={ruleForm}
        setForm={setRuleForm}
        templates={templates}
        saving={savingRule}
        onClose={() => setRuleDialogOpen(false)}
        onSave={saveRule}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
